import { Form, Formik } from "formik";
import { useLocation } from "react-router-dom";
import { MosquePrefill } from "pages/SuperAdminPages/InquiriesPage/inquiries";
import useFetch from "hooks/useFetch";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AddressField,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Column,
  ConfirmDialog,
  EmptyState,
  FormikAddressField,
  FormikSelect,
  FormikTextField,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Tabs,
  TextField,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import styles from "./AddMosquePage.module.scss";
import ReplacesMosqueField from "./ReplacesMosqueField";
import { formatOrgNumber } from "../../AdminLayout/menuConfig";

type MosqueTab = "search" | "requests" | "create";

/** «Ingen» eller tom Asr-metode: appene, TV-ene og backend bruker da blandet Asr. */
const hasNoAsrMethod = (m: { asrMethod?: string | null }) =>
  !m.asrMethod || m.asrMethod === "NONE";

const asrLabel = (method?: string | null) =>
  method === "ONE_X"
    ? "1×"
    : method === "TWO_X"
      ? "2×"
      : method === "MIXED"
        ? "Blandet"
        : method || "—";

interface NewMosqueFormValues {
  orgNr: string;
  name: string;
  orgName: string;
  logo: string;
  prayerTimeTable: string;
  districtCode: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  lat: string;
  lon: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  locationId: string;
  asrMethod: string;
  merchantId: string;
  mapOnly: boolean;
}

interface Location {
  id: number;
  name: string;
  prayerTimesTables: Array<{ id: number; name: string }>;
}

interface Mosque {
  id: number;
  logo: string;
  orgNr: string;
  name: string;
  orgName: string;
  districtCode: number;
  mapOnly: boolean;
  streetAddress: string;
  postalCode: string;
  city: string;
  lat: number;
  lon: number;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  locationId: number;
  prayerTimeTable: number;
  prayerTimeTableName: string;
  locationName: string;
  asrMethod: string;
  merchantId: string;
  cached: boolean;
  cacheTimestamp: string;
}

interface EditMosqueForm {
  orgNr: string;
  name: string;
  orgName: string;
  logo: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  districtCode: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  lat: string;
  lon: string;
  locationId: string;
  prayerTimeTable: string;
  asrMethod: string;
  merchantId: string;
  mapOnly: boolean;
}

const emptyEditForm = (): EditMosqueForm => ({
  orgNr: "",
  name: "",
  orgName: "",
  logo: "",
  streetAddress: "",
  postalCode: "",
  city: "",
  districtCode: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  lat: "",
  lon: "",
  locationId: "",
  prayerTimeTable: "",
  asrMethod: "NONE",
  merchantId: "",
  mapOnly: false,
});

const validationSchema = Yup.object({
  orgNr: Yup.string().required("Organisasjonsnummer er påkrevd"),
  name: Yup.string().required("Moské navn er påkrevd"),
  orgName: Yup.string().required("Organisasjonsnavn er påkrevd"),
  streetAddress: Yup.string().required("Adresse er påkrevd"),
  postalCode: Yup.string().required("Postnummer er påkrevd"),
  city: Yup.string().required("Sted er påkrevd"),
  contactName: Yup.string().required("Kontaktperson er påkrevd"),
  contactPhone: Yup.string().required("Telefonnummer er påkrevd"),
  contactEmail: Yup.string()
    .email("Ugyldig e-post")
    .required("E-post er påkrevd"),
});

const display = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  return String(value);
};

const logoPreviewUrl = (
  logo?: string | null,
  bust?: string | number,
): string | null => {
  const url = logo?.trim();
  if (!url) return null;
  const separator = url.includes("?") ? "&" : "?";
  return bust != null ? `${url}${separator}v=${bust}` : url;
};

const mosqueToEditForm = (mosque: Mosque): EditMosqueForm => ({
  orgNr: mosque.orgNr || "",
  name: mosque.name || "",
  orgName: mosque.orgName || "",
  logo: mosque.logo || "",
  streetAddress: mosque.streetAddress || "",
  postalCode: mosque.postalCode || "",
  city: mosque.city || "",
  districtCode: mosque.districtCode != null ? String(mosque.districtCode) : "",
  contactName: mosque.contactName || "",
  contactPhone: mosque.contactPhone || "",
  contactEmail: mosque.contactEmail || "",
  lat: mosque.lat != null ? String(mosque.lat) : "",
  lon: mosque.lon != null ? String(mosque.lon) : "",
  locationId: mosque.locationId != null ? String(mosque.locationId) : "",
  prayerTimeTable:
    mosque.prayerTimeTable != null ? String(mosque.prayerTimeTable) : "",
  asrMethod: mosque.asrMethod || "NONE",
  merchantId: mosque.merchantId || "",
  mapOnly: Boolean(mosque.mapOnly),
});

const AddMosquePage: React.FC = () => {
  const { execute, lastError } = useFetch();
  // «Opprett moské» fra et moskéforslag under Henvendelser: feltene er forhåndsutfylt.
  const navState = useLocation().state as {
    prefill?: Partial<MosquePrefill>;
    search?: string;
  } | null;
  const prefill = navState?.prefill;
  // «Åpne» fra Eksterne kilder: søk opp (og velg) moskeen.
  const openSearch = navState?.search;
  const [activeTab, setActiveTab] = useState<MosqueTab>(
    prefill ? "create" : "search",
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [prayerTimesTables, setPrayerTimesTables] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [mosques, setMosques] = useState<Mosque[]>([]);
  const [mosquesLoading, setMosquesLoading] = useState(false);
  const [selectedMosque, setSelectedMosque] = useState<Mosque | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [mosqueSearchTerm, setMosqueSearchTerm] = useState(openSearch ?? "");
  /** Vis bare moskeer uten valgt Asr-metode («Ingen» eller tom), som får blandet Asr. */
  const [onlyMissingAsr, setOnlyMissingAsr] = useState(false);
  const [editForm, setEditForm] = useState<EditMosqueForm>(emptyEditForm());
  const [editLogoFile, setEditLogoFile] = useState<File | null>(null);
  const [editLogoPreview, setEditLogoPreview] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<Mosque[]>([]);
  const toast = useToast();
  const confirm = useConfirm();
  const editLogoInputRef = useRef<HTMLInputElement>(null);
  const createLogoInputRef = useRef<HTMLInputElement>(null);
  const [logoBust, setLogoBust] = useState(() => Date.now());

  const refreshMosques = useCallback(async () => {
    setMosquesLoading(true);
    try {
      const mosquesResult = (await execute("/api/v1/mosques", "GET")) as
        | Mosque[]
        | null;
      if (mosquesResult) {
        const sorted = [...mosquesResult].sort((a, b) =>
          (a.orgName || a.name || "").localeCompare(b.orgName || b.name || ""),
        );
        setMosques(sorted);
        setSelectedMosque((prev) => {
          if (!prev) return null;
          return sorted.find((m) => m.id === prev.id) || null;
        });
        setLogoBust(Date.now());
      }
    } finally {
      setMosquesLoading(false);
    }
  }, [execute]);

  useEffect(() => {
    void refreshMosques();
  }, []);

  useEffect(() => {
    const fetchPendingRequests = async () => {
      try {
        const result = (await execute(
          "/api/v1/mosques/approval/pending",
          "GET",
        )) as Mosque[];
        setPendingRequests(result || []);
      } catch (error) {
        console.error("Feil ved henting av ventende forespørsler:", error);
      }
    };
    void fetchPendingRequests();
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationResult = (await execute(
          "/api/v1/locations",
          "GET",
        )) as Location[];
        if (locationResult) {
          setLocations(
            [...locationResult].sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      } catch (error) {
        console.error("Feil ved henting av lokasjoner:", error);
      }
    };
    void fetchLocations();
  }, []);

  const missingAsrCount = useMemo(
    () => mosques.filter(hasNoAsrMethod).length,
    [mosques],
  );

  const filteredMosques = useMemo(() => {
    const base = onlyMissingAsr ? mosques.filter(hasNoAsrMethod) : mosques;
    if (!mosqueSearchTerm.trim()) return base;
    const lower = mosqueSearchTerm.toLowerCase().trim();
    return base.filter((m) => {
      const haystack = [m.name, m.orgName, m.orgNr, m.city, m.streetAddress]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [mosques, mosqueSearchTerm, onlyMissingAsr]);

  const selectMosque = (mosque: Mosque) => {
    setSelectedMosque(mosque);
    setIsEditing(false);
    setEditLogoFile(null);
    const location = locations.find((l) => l.id === mosque.locationId);
    setPrayerTimesTables(location?.prayerTimesTables || []);
  };

  // Åpnet med en bestemt moské (org.nr.): velg den når lista er lastet.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openSearch || openedRef.current || mosques.length === 0) return;
    const match = mosques.find((m) => m.orgNr === openSearch.trim());
    if (match) {
      openedRef.current = true;
      selectMosque(match);
    }
  }, [mosques, openSearch]);

  const startEditing = () => {
    if (!selectedMosque) return;
    const form = mosqueToEditForm(selectedMosque);
    setEditForm(form);
    setEditLogoFile(null);
    setEditLogoPreview(logoPreviewUrl(selectedMosque.logo, logoBust));
    const location = locations.find((l) => l.id === selectedMosque.locationId);
    setPrayerTimesTables(location?.prayerTimesTables || []);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditLogoFile(null);
    setEditLogoPreview(null);
  };

  const updateEditField = <K extends keyof EditMosqueForm>(
    key: K,
    value: EditMosqueForm[K],
  ) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditLogoFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setEditLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setEditLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSaveMosqueEdits = async () => {
    if (!selectedMosque) return;
    if (!editForm.orgNr.trim()) {
      toast.error("Organisasjonsnummer er påkrevd.");
      return;
    }
    if (!editForm.lat.trim() || !editForm.lon.trim()) {
      toast.error("Breddegrad og lengdegrad må fylles ut.");
      return;
    }

    setEditSaving(true);
    try {
      let logoUrlValue = editForm.logo.trim() || null;
      if (editLogoFile) {
        const formData = new FormData();
        formData.append("file", editLogoFile);
        const uploadResult = (await execute(
          `/api/v1/files/upload/LOGO/${editForm.orgNr.trim()}`,
          "POST",
          formData,
        )) as { filePath?: string } | null;
        if (uploadResult?.filePath) {
          logoUrlValue = uploadResult.filePath;
        }
      }

      const payload = {
        orgNr: editForm.orgNr.trim(),
        name: editForm.name.trim() || null,
        orgName: editForm.orgName.trim() || null,
        logo: logoUrlValue,
        streetAddress: editForm.streetAddress.trim() || null,
        postalCode: editForm.postalCode.trim() || null,
        city: editForm.city.trim() || null,
        districtCode: editForm.districtCode
          ? Number(editForm.districtCode)
          : null,
        contactName: editForm.contactName.trim() || null,
        contactPhone: editForm.contactPhone.trim() || null,
        contactEmail: editForm.contactEmail.trim() || null,
        lat: Number(editForm.lat),
        lon: Number(editForm.lon),
        locationId: editForm.locationId ? Number(editForm.locationId) : null,
        prayerTimeTable: editForm.prayerTimeTable
          ? Number(editForm.prayerTimeTable)
          : null,
        asrMethod: editForm.asrMethod.trim() || null,
        merchantId: editForm.merchantId.trim() || null,
        mapOnly: editForm.mapOnly,
      };

      const saved = await execute(
        `/api/v1/mosques/${selectedMosque.orgNr}`,
        "PATCH",
        payload,
      );
      // execute gir null ved feil (kaster ikke): vis serverens feil i stedet for «oppdatert».
      if (!saved) {
        const err = lastError();
        toast.error(
          `Kunne ikke lagre moskeen${err?.code ? ` (feil ${err.code})` : ""}${err?.message ? `: ${err.message}` : "."}`,
        );
        return;
      }
      await refreshMosques();
      setIsEditing(false);
      toast.success("Moskéinformasjon oppdatert");
    } catch (error) {
      console.error("Feil ved oppdatering av moské:", error);
      toast.error("Kunne ikke oppdatere moskéinformasjonen.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteMosque = async () => {
    if (!selectedMosque) return;
    await execute(`/api/v1/mosques/${selectedMosque.id}`, "DELETE");
    // execute gir null også ved 204, så sjekk feilen: før ble moskeen fjernet fra lista og «slettet» vist selv om
    // serveren avviste slettingen, og moskeen «kom tilbake» ved neste lasting.
    const err = lastError();
    if (err) {
      setShowDeleteModal(false);
      toast.error(
        `Kunne ikke slette moskeen${err.code ? ` (feil ${err.code})` : ""}${err.message ? `: ${err.message}` : "."}`,
      );
      return;
    }
    setMosques((prev) => prev.filter((m) => m.id !== selectedMosque.id));
    setSelectedMosque(null);
    setIsEditing(false);
    setShowDeleteModal(false);
    toast.success("Moské slettet");
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      const reader = new FileReader();
      reader.onload = () => setLogoUrl(reader.result as string);
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const handleApproveRequest = async (id: number) => {
    try {
      await execute(`/api/v1/mosques/approval/approve/${id}`, "POST");
      setPendingRequests((prev) => prev.filter((req) => req.id !== id));
      toast.success("Moskéen er godkjent");
      await refreshMosques();
    } catch {
      toast.error("Kunne ikke godkjenne forespørselen.");
    }
  };

  const handleRejectRequest = async (id: number) => {
    const request = pendingRequests.find((req) => req.id === id);
    const ok = await confirm({
      title: "Avvis forespørselen?",
      message: (
        <>
          Forespørselen fra <b>{request?.name || request?.orgName}</b> slettes.
        </>
      ),
      confirmLabel: "Avvis",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await execute(`/api/v1/mosques/approval/reject/${id}`, "DELETE");
      setPendingRequests((prev) => prev.filter((req) => req.id !== id));
      toast.success("Moskéforespørselen er avvist");
    } catch {
      toast.error("Kunne ikke avvise forespørselen.");
    }
  };

  const handleSubmit = async (values: NewMosqueFormValues) => {
    if (!values.locationId) {
      toast.error("Lokasjon er påkrevd.");
      return;
    }
    if (!values.lat.trim() || !values.lon.trim()) {
      toast.error("Breddegrad og lengdegrad er påkrevd.");
      return;
    }

    const mosquePayload = {
      orgNr: values.orgNr.trim(),
      name: values.name.trim() || null,
      orgName: values.orgName.trim() || null,
      districtCode: values.districtCode ? Number(values.districtCode) : null,
      mapOnly: values.mapOnly,
      streetAddress: values.streetAddress.trim() || null,
      postalCode: values.postalCode.trim() || null,
      city: values.city.trim() || null,
      lat: Number(values.lat),
      lon: Number(values.lon),
      contactName: values.contactName.trim() || null,
      contactPhone: values.contactPhone.trim() || null,
      contactEmail: values.contactEmail.trim() || null,
      locationId: Number(values.locationId),
      prayerTimeTable: values.prayerTimeTable
        ? Number(values.prayerTimeTable)
        : 1,
      asrMethod: values.asrMethod.trim() || "NONE",
      merchantId: values.merchantId.trim() || null,
      logo: values.logo.trim() || null,
    };

    try {
      await execute("/api/v1/mosques", "POST", mosquePayload);
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        await execute(
          `/api/v1/files/upload/LOGO/${values.orgNr.trim()}`,
          "POST",
          formData,
        );
      }
      toast.success("Moské opprettet");
      setSelectedFile(null);
      setLogoUrl(null);
      await refreshMosques();
      setActiveTab("search");
      setMosqueSearchTerm(values.orgNr.trim());
    } catch {
      toast.error("En feil oppstod under oppretting av moské.");
    }
  };

  const selectedLogoSrc = logoPreviewUrl(selectedMosque?.logo, logoBust);

  const detailRows: Array<{ label: string; value: React.ReactNode }> =
    selectedMosque
      ? [
          { label: "ID", value: display(selectedMosque.id) },
          { label: "Org.nr.", value: display(selectedMosque.orgNr) },
          { label: "Navn", value: display(selectedMosque.name) },
          { label: "Org.navn", value: display(selectedMosque.orgName) },
          { label: "Adresse", value: display(selectedMosque.streetAddress) },
          { label: "Postnr.", value: display(selectedMosque.postalCode) },
          { label: "By", value: display(selectedMosque.city) },
          {
            label: "District code",
            value: display(selectedMosque.districtCode),
          },
          { label: "Kontakt", value: display(selectedMosque.contactName) },
          { label: "Telefon", value: display(selectedMosque.contactPhone) },
          { label: "E-post", value: display(selectedMosque.contactEmail) },
          {
            label: "Lokasjon",
            value: `${display(selectedMosque.locationName)} (#${display(selectedMosque.locationId)})`,
          },
          {
            label: "Bønnetabell",
            value: `${display(selectedMosque.prayerTimeTableName)} (#${display(selectedMosque.prayerTimeTable)})`,
          },
          { label: "Asr-metode", value: display(selectedMosque.asrMethod) },
          { label: "Merchant ID", value: display(selectedMosque.merchantId) },
          { label: "Kun kart", value: display(selectedMosque.mapOnly) },
          {
            label: "Koordinater",
            value: `${display(selectedMosque.lat)}, ${display(selectedMosque.lon)}`,
          },
          {
            label: "Logo-URL",
            value: selectedMosque.logo ? (
              <a
                className={styles.link}
                href={selectedMosque.logo}
                target="_blank"
                rel="noreferrer"
              >
                {selectedMosque.logo}
              </a>
            ) : (
              "—"
            ),
          },
          { label: "Cachet", value: display(selectedMosque.cached) },
          {
            label: "Cache-tidspunkt",
            value: display(selectedMosque.cacheTimestamp),
          },
        ]
      : [];

  const logoThumb = (src: string | null, size: "sm" | "md" | "lg") => (
    <span
      className={`${styles.thumb} ${
        size === "sm"
          ? styles.thumbSm
          : size === "md"
            ? styles.thumbMd
            : styles.thumbLg
      } ${src ? styles.thumbFilled : ""}`}
    >
      {src ? (
        <img src={src} alt="" />
      ) : (
        <Icon name="mosque" size={size === "sm" ? 16 : 24} />
      )}
    </span>
  );

  const mosqueColumns: Array<Column<Mosque>> = [
    {
      key: "logo",
      header: "",
      width: 52,
      render: (m) => logoThumb(logoPreviewUrl(m.logo, logoBust), "sm"),
    },
    { key: "name", header: "Navn", render: (m) => <b>{m.name || "—"}</b> },
    {
      key: "orgName",
      header: "Org.navn",
      render: (m) => <span className={styles.muted}>{m.orgName || "—"}</span>,
    },
    {
      key: "orgNr",
      header: "Org.nr.",
      numeric: true,
      render: (m) => formatOrgNumber(m.orgNr),
    },
    { key: "city", header: "By", render: (m) => m.city || "—" },
    {
      key: "asrMethod",
      header: "Asr",
      render: (m) =>
        hasNoAsrMethod(m) ? (
          <Badge tone="gold">Ingen</Badge>
        ) : (
          <span className={styles.muted}>{asrLabel(m.asrMethod)}</span>
        ),
    },
  ];

  const tableOptions = prayerTimesTables.map((table) => (
    <option key={table.id} value={table.id}>
      {table.name} (#{table.id})
    </option>
  ));

  return (
    <>
      <PageHeader
        title="Moskeer"
        subtitle="Søk, rediger alle felter, behandle forespørsler og opprett nye moskeer."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={refreshMosques}
            loading={mosquesLoading}
          >
            {mosquesLoading ? "Oppdaterer …" : "Oppdater"}
          </Button>
        }
      />

      <Tabs
        label="Moskeer"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { value: "search", label: "Søk og rediger" },
          {
            value: "requests",
            label: "Forespørsler",
            extra: pendingRequests.length > 0 && (
              <Badge tone="rose" className={styles.tabBadge}>
                {pendingRequests.length}
              </Badge>
            ),
          },
          { value: "create", label: "Ny moské" },
        ]}
      />

      {activeTab === "search" && (
        <>
          <div className={styles.filters}>
            <TextField
              aria-label="Søk etter moské"
              type="search"
              placeholder="Søk på org.nr., navn, organisasjonsnavn, by eller gate …"
              className={styles.search}
              fieldClassName={styles.searchField}
              value={mosqueSearchTerm}
              onChange={(e) => setMosqueSearchTerm(e.target.value)}
              autoFocus
            />
            <Button
              variant={onlyMissingAsr ? "primary" : "secondary"}
              size="sm"
              onClick={() => setOnlyMissingAsr((v) => !v)}
              aria-pressed={onlyMissingAsr}
              title="Moskeer med Asr-metode «Ingen» eller tom. De får blandet Asr (1× skygge i sommertid, 2× i vintertid)."
            >
              Asr: Ingen ({missingAsrCount})
            </Button>
            <span className={styles.muted}>
              Viser {filteredMosques.length} av {mosques.length}
            </span>
          </div>

          <div className={styles.split}>
            <Card className={styles.listCard}>
              {mosquesLoading && mosques.length === 0 ? (
                <div className={styles.skeletons}>
                  {Array.from({ length: 7 }, (_, i) => (
                    <Skeleton key={i} height={40} />
                  ))}
                </div>
              ) : (
                <Table
                  columns={mosqueColumns}
                  rows={filteredMosques}
                  rowKey={(m) => m.id}
                  onRowClick={(m) => selectMosque(m)}
                  rowState={(m) =>
                    selectedMosque?.id === m.id ? "on" : undefined
                  }
                  empty={
                    <EmptyState icon="search" title="Ingen treff">
                      Ingen moskeer passer søket.
                    </EmptyState>
                  }
                />
              )}
            </Card>

            <Card className={styles.detailCard}>
              {!selectedMosque ? (
                <EmptyState icon="mosque" title="Ingen moské valgt">
                  Velg en moské i listen for å se og redigere alle felter.
                </EmptyState>
              ) : isEditing ? (
                <>
                  <div className={styles.detailHead}>
                    <b className={styles.detailTitle}>Rediger moské</b>
                  </div>

                  <div className={styles.logoRow}>
                    {logoThumb(editLogoPreview, "md")}
                    <div className={styles.logoFields}>
                      <TextField
                        label="Logo-URL"
                        value={editForm.logo}
                        onChange={(e) =>
                          updateEditField("logo", e.target.value)
                        }
                        placeholder="https://…/LOGO/{orgNr}"
                      />
                      <div className={styles.inline}>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<Icon name="upload" size={14} />}
                          onClick={() => editLogoInputRef.current?.click()}
                        >
                          Last opp ny logo
                        </Button>
                        {editLogoFile && (
                          <span className={styles.muted}>
                            {editLogoFile.name}
                          </span>
                        )}
                      </div>
                      <input
                        ref={editLogoInputRef}
                        type="file"
                        accept="image/*"
                        className={styles.fileInput}
                        onChange={handleEditLogoFile}
                      />
                    </div>
                  </div>

                  <div className={ui.g2}>
                    <TextField
                      label="Org.nr."
                      required
                      value={editForm.orgNr}
                      onChange={(e) => updateEditField("orgNr", e.target.value)}
                    />
                    <TextField
                      label="Moskénavn"
                      value={editForm.name}
                      onChange={(e) => updateEditField("name", e.target.value)}
                    />
                  </div>
                  <TextField
                    label="Org.navn"
                    value={editForm.orgName}
                    onChange={(e) => updateEditField("orgName", e.target.value)}
                  />
                  <div className={styles.addressGrid}>
                    <AddressField
                      label="Adresse"
                      value={editForm.streetAddress}
                      onChange={(v) => updateEditField("streetAddress", v)}
                      onPick={(a) =>
                        setEditForm((prev) => ({
                          ...prev,
                          streetAddress: a.streetAddress,
                          postalCode: a.postalCode || prev.postalCode,
                          city: a.city || prev.city,
                          lat: String(a.lat),
                          lon: String(a.lon),
                        }))
                      }
                    />
                    <TextField
                      label="Postnr."
                      value={editForm.postalCode}
                      onChange={(e) =>
                        updateEditField("postalCode", e.target.value)
                      }
                    />
                  </div>
                  <div className={ui.g2}>
                    <TextField
                      label="By"
                      value={editForm.city}
                      onChange={(e) => updateEditField("city", e.target.value)}
                    />
                    <TextField
                      label="District code"
                      type="number"
                      value={editForm.districtCode}
                      onChange={(e) =>
                        updateEditField("districtCode", e.target.value)
                      }
                    />
                  </div>
                  <div className={ui.g2}>
                    <TextField
                      label="Kontakt"
                      value={editForm.contactName}
                      onChange={(e) =>
                        updateEditField("contactName", e.target.value)
                      }
                    />
                    <TextField
                      label="Telefon"
                      value={editForm.contactPhone}
                      onChange={(e) =>
                        updateEditField("contactPhone", e.target.value)
                      }
                    />
                  </div>
                  <TextField
                    label="E-post"
                    value={editForm.contactEmail}
                    onChange={(e) =>
                      updateEditField("contactEmail", e.target.value)
                    }
                  />
                  <div className={ui.g2}>
                    <TextField
                      label="Breddegrad (lat)"
                      required
                      type="number"
                      step="any"
                      value={editForm.lat}
                      onChange={(e) => updateEditField("lat", e.target.value)}
                    />
                    <TextField
                      label="Lengdegrad (lon)"
                      required
                      type="number"
                      step="any"
                      value={editForm.lon}
                      onChange={(e) => updateEditField("lon", e.target.value)}
                    />
                  </div>
                  <div className={ui.g2}>
                    <Select
                      label="Lokasjon"
                      value={editForm.locationId}
                      onChange={(e) => {
                        const location = locations.find(
                          (l) => l.id === Number(e.target.value),
                        );
                        updateEditField("locationId", e.target.value);
                        setPrayerTimesTables(location?.prayerTimesTables || []);
                      }}
                    >
                      <option value="">Velg lokasjon</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </Select>
                    <Select
                      label="Bønnetabell"
                      value={editForm.prayerTimeTable}
                      onChange={(e) =>
                        updateEditField("prayerTimeTable", e.target.value)
                      }
                    >
                      <option value="">Velg tabell</option>
                      {tableOptions}
                    </Select>
                  </div>
                  <div className={ui.g2}>
                    <Select
                      label="Asr-metode"
                      value={editForm.asrMethod}
                      onChange={(e) =>
                        updateEditField("asrMethod", e.target.value)
                      }
                    >
                      <option value="NONE">Ingen (NONE) – blandet</option>
                      <option value="ONE_X">ONE_X</option>
                      <option value="TWO_X">TWO_X</option>
                    </Select>
                    <TextField
                      label="Merchant ID (Vipps)"
                      value={editForm.merchantId}
                      onChange={(e) =>
                        updateEditField("merchantId", e.target.value)
                      }
                    />
                  </div>
                  <Checkbox
                    id="editMapOnly"
                    label="Kun kart (mapOnly)"
                    checked={editForm.mapOnly}
                    onChange={(e) =>
                      updateEditField("mapOnly", e.target.checked)
                    }
                  />

                  <div className={styles.editActions}>
                    <Button
                      variant="secondary"
                      onClick={cancelEditing}
                      disabled={editSaving}
                    >
                      Avbryt
                    </Button>
                    <Button
                      icon={<Icon name="check" size={16} />}
                      onClick={handleSaveMosqueEdits}
                      loading={editSaving}
                    >
                      {editSaving ? "Lagrer …" : "Lagre alle felter"}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.detailHead}>
                    {logoThumb(selectedLogoSrc, "md")}
                    <span className={styles.detailTitleBlock}>
                      <b className={styles.detailTitle}>
                        {selectedMosque.name || selectedMosque.orgName}
                      </b>
                      <span className={styles.inline}>
                        <Badge tone="grey">
                          {formatOrgNumber(selectedMosque.orgNr)}
                        </Badge>
                        {selectedMosque.logo ? (
                          <Badge tone="mint">Har logo</Badge>
                        ) : (
                          <Badge tone="rose">Mangler logo</Badge>
                        )}
                        {selectedMosque.mapOnly && (
                          <Badge tone="gold">Kun kart</Badge>
                        )}
                      </span>
                    </span>
                  </div>

                  <div className={styles.inline}>
                    <Button
                      icon={<Icon name="edit" size={16} />}
                      onClick={startEditing}
                    >
                      Rediger alle felter
                    </Button>
                    <span className={styles.pushRight}>
                      <Button
                        variant="danger"
                        icon={<Icon name="trash" size={16} />}
                        onClick={() => setShowDeleteModal(true)}
                      >
                        Slett
                      </Button>
                    </span>
                  </div>

                  <dl className={styles.dl}>
                    {detailRows.map((row) => (
                      <React.Fragment key={row.label}>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>

                  <ReplacesMosqueField
                    orgNr={selectedMosque.orgNr}
                    mosques={mosques}
                  />
                </>
              )}
            </Card>
          </div>
        </>
      )}

      {activeTab === "requests" && (
        <>
          <span className={styles.muted}>
            Forespørsler sendt via ibønn.no. Godkjente moskeer blir synlige i
            appen.
          </span>
          {pendingRequests.length === 0 ? (
            <Card>
              <EmptyState icon="inbox" title="Ingen ventende forespørsler" />
            </Card>
          ) : (
            pendingRequests.map((request) => (
              <Card key={request.id} className={styles.requestCard}>
                <span className={styles.requestIcon} aria-hidden="true">
                  <Icon name="mosque" size={22} />
                </span>
                <div className={styles.requestText}>
                  <span className={styles.inline}>
                    <b className={styles.requestName}>
                      {request.name || request.orgName}
                    </b>
                    <Badge tone="grey">
                      Org.nr. {formatOrgNumber(request.orgNr)}
                    </Badge>
                  </span>
                  <span className={styles.requestMeta}>
                    {[
                      request.contactName,
                      request.contactEmail,
                      request.contactPhone,
                      `${request.streetAddress}, ${request.postalCode} ${request.city}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <Button
                  variant="danger"
                  icon={<Icon name="x" size={16} />}
                  onClick={() => handleRejectRequest(request.id)}
                >
                  Avvis
                </Button>
                <Button
                  icon={<Icon name="check" size={16} />}
                  onClick={() => handleApproveRequest(request.id)}
                >
                  Godkjenn
                </Button>
              </Card>
            ))
          )}
        </>
      )}

      {activeTab === "create" && (
        <Formik
          initialValues={{
            orgNr: "",
            name: "",
            orgName: "",
            logo: "",
            prayerTimeTable: "",
            districtCode: "",
            streetAddress: "",
            postalCode: "",
            city: "",
            lat: "",
            lon: "",
            contactName: "",
            contactPhone: "",
            contactEmail: "",
            asrMethod: "NONE",
            locationId: "",
            merchantId: "",
            mapOnly: false,
            ...prefill,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className={styles.createForm} noValidate>
              <Card title="Logo">
                <div className={styles.logoRow}>
                  {logoThumb(logoUrl || values.logo || null, "lg")}
                  <div className={styles.logoFields}>
                    <FormikTextField
                      id="createLogoUrl"
                      name="logo"
                      label="Logo-URL"
                      placeholder="https://…/LOGO/{orgNr}"
                    />
                    <div className={styles.inline}>
                      <Button
                        variant="secondary"
                        icon={<Icon name="upload" size={16} />}
                        onClick={() => createLogoInputRef.current?.click()}
                      >
                        Last opp logo
                      </Button>
                      <span className={styles.muted}>
                        {selectedFile ? selectedFile.name : "eller lim inn URL"}
                      </span>
                    </div>
                    <input
                      ref={createLogoInputRef}
                      type="file"
                      id="createLogoUpload"
                      accept="image/*"
                      className={styles.fileInput}
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </Card>

              <Card title="Moskeen">
                <div className={ui.g3}>
                  <FormikTextField
                    id="orgNr"
                    name="orgNr"
                    label="Organisasjonsnummer"
                    required
                  />
                  <FormikTextField
                    id="name"
                    name="name"
                    label="Moskénavn"
                    required
                  />
                  <FormikTextField
                    id="orgName"
                    name="orgName"
                    label="Organisasjonsnavn"
                    required
                  />
                </div>
                <div className={ui.g3}>
                  <FormikSelect
                    id="locationId"
                    name="locationId"
                    label="Lokasjon"
                    required
                    onChange={(e) => {
                      const selectedLocation = locations.find(
                        (location) =>
                          location.id === parseInt(e.target.value, 10),
                      );
                      setFieldValue("locationId", e.target.value);
                      setPrayerTimesTables(
                        selectedLocation?.prayerTimesTables || [],
                      );
                    }}
                  >
                    <option value="">Velg en lokasjon</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </FormikSelect>
                  <FormikSelect
                    id="prayerTimeTable"
                    name="prayerTimeTable"
                    label="Bønnetabell"
                    disabled={prayerTimesTables.length === 0}
                    help="Standard (#1) brukes hvis ingen velges."
                  >
                    <option value="">
                      {prayerTimesTables.length === 0
                        ? "Velg lokasjon først"
                        : "Velg en tabell"}
                    </option>
                    {tableOptions}
                  </FormikSelect>
                  <FormikSelect
                    id="asrMethod"
                    name="asrMethod"
                    label="Asr-metode"
                  >
                    <option value="NONE">Ingen (NONE) – blandet</option>
                    <option value="ONE_X">ONE_X</option>
                    <option value="TWO_X">TWO_X</option>
                  </FormikSelect>
                </div>
                <div className={ui.g3}>
                  <FormikTextField
                    id="merchantId"
                    name="merchantId"
                    label="Merchant ID (Vipps)"
                  />
                  <FormikTextField
                    id="districtCode"
                    name="districtCode"
                    type="number"
                    label="District code"
                  />
                  <div className={styles.checkCell}>
                    <Checkbox
                      name="mapOnly"
                      label="Kun kart (mapOnly)"
                      checked={values.mapOnly}
                      onChange={(e) =>
                        setFieldValue("mapOnly", e.target.checked)
                      }
                    />
                  </div>
                </div>
              </Card>

              <Card
                title="Adresse og koordinater"
                subtitle="Velg adressen fra forslagene, så fylles postnummer, sted og koordinater inn."
              >
                <div className={ui.g3}>
                  <FormikAddressField
                    id="streetAddress"
                    name="streetAddress"
                    label="Adresse"
                    required
                    onPick={(a) => {
                      if (a.postalCode)
                        setFieldValue("postalCode", a.postalCode);
                      if (a.city) setFieldValue("city", a.city);
                      setFieldValue("lat", String(a.lat));
                      setFieldValue("lon", String(a.lon));
                    }}
                  />
                  <FormikTextField
                    id="postalCode"
                    name="postalCode"
                    label="Postnummer"
                    required
                  />
                  <FormikTextField
                    id="city"
                    name="city"
                    label="Sted"
                    required
                  />
                </div>
                <div className={ui.g3}>
                  <FormikTextField
                    id="lat"
                    name="lat"
                    type="number"
                    step="any"
                    label="Breddegrad (lat)"
                    placeholder="59.91"
                    required
                  />
                  <FormikTextField
                    id="lon"
                    name="lon"
                    type="number"
                    step="any"
                    label="Lengdegrad (lon)"
                    placeholder="10.75"
                    required
                  />
                </div>
              </Card>

              <Card title="Kontakt">
                <div className={ui.g3}>
                  <FormikTextField
                    id="contactName"
                    name="contactName"
                    label="Kontaktperson"
                    required
                  />
                  <FormikTextField
                    id="contactPhone"
                    name="contactPhone"
                    label="Telefon"
                    required
                  />
                  <FormikTextField
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    label="E-post"
                    required
                  />
                </div>
              </Card>

              <div className={styles.submitRow}>
                <Button
                  type="submit"
                  icon={<Icon name="plus" size={16} />}
                  loading={isSubmitting}
                >
                  Opprett moské
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}

      <ConfirmDialog
        open={showDeleteModal && !!selectedMosque}
        title="Slett moské?"
        tone="danger"
        confirmLabel="Slett moské"
        requireText={selectedMosque?.orgName ?? ""}
        requireTextLabel="Skriv organisasjonsnavnet for å bekrefte"
        onConfirm={handleDeleteMosque}
        onCancel={() => setShowDeleteModal(false)}
      >
        <Alert tone="error">
          Moskeen og alle data knyttet til den slettes permanent.
        </Alert>
        <span className={styles.muted}>
          Må være nøyaktig: <b>{selectedMosque?.orgName}</b>
        </span>
      </ConfirmDialog>
    </>
  );
};

export default AddMosquePage;
